/**
 * VD Hero carousel — crossfade slideshow for sections/vd-hero.liquid.
 *
 * Only loaded when the section renders more than one "slide" block (see the
 * `{%- if slide_count > 1 -%}` guard around the <script> tag in
 * vd-hero.liquid), so a single-slide hero never pays for this file.
 *
 * Behavior:
 * - Crossfade between `.vd-hero__slide` layers via opacity (CSS handles the
 *   800ms transition; this file only toggles `.is-active`).
 * - Autoplay every 6.5s, but only when there's more than one slide, the
 *   visitor hasn't requested reduced motion, and the tab is visible —
 *   paused on `visibilitychange` to hidden, resumed on visible.
 * - Per-slide video: play the active slide's video (if it has one) and
 *   pause every other slide's video, so at most one video decodes/plays
 *   at a time.
 * - Copy crossfade: eyebrow/heading/sub only fade+swap when the incoming
 *   slide's resolved text actually differs from what's currently shown —
 *   a slide with no copy override resolves to the same section-default
 *   text, so back-to-back slides that share the same resolved copy don't
 *   flash/refade for no reason.
 * - Dots: minimal 44px-target buttons, bottom-right, keyboard-operable
 *   (native <button>, Enter/Space activates).
 * - Touch: horizontal swipe on the media area advances/retreats a slide.
 *
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var AUTOPLAY_INTERVAL_MS = 6500;
  // Must match --vd-hero-fade in velodream.css: how long the outgoing slide
  // takes to fade off the top of the stack.
  var CROSSFADE_MS = 800;
  // Copy swaps at the crossfade MIDPOINT rather than racing ahead of it. The
  // old 220ms had the words fully resolved by ~440ms over an image still
  // visibly changing until 800ms.
  var TEXT_FADE_MS = CROSSFADE_MS / 2;
  var SWIPE_THRESHOLD_PX = 40;

  function initCarousel(section) {
    var slides = Array.prototype.slice.call(section.querySelectorAll('.vd-hero__slide'));
    var dots = Array.prototype.slice.call(section.querySelectorAll('.vd-hero__dot'));
    var media = section.querySelector('.vd-hero__media');
    var eyebrowEl = section.querySelector('.vd-hero__eyebrow');
    var headingEl = section.querySelector('.vd-hero__heading');
    var subEl = section.querySelector('.vd-hero__sub');

    if (slides.length < 2) return;

    // Publish the timings CSS needs so this file stays the single source for
    // them. The dot progress bar has to finish exactly as the slide changes;
    // hard-coding 6500ms in the stylesheet too would let the two drift apart
    // on the next tuning pass, and a progress bar that disagrees with the
    // rotation is worse than none. The stylesheet keeps matching fallbacks so
    // it still behaves if this file never runs.
    section.style.setProperty('--vd-hero-dwell', AUTOPLAY_INTERVAL_MS + 'ms');
    section.style.setProperty('--vd-hero-fade', CROSSFADE_MS + 'ms');
    // Outlast the dwell so the drift never visibly parks before the swap.
    section.style.setProperty('--vd-hero-drift', AUTOPLAY_INTERVAL_MS + CROSSFADE_MS + 100 + 'ms');

    // Hand the copy fields back to their transitions once the entrance
    // animation has played. `vd-rise` runs `forwards`, and a filled animation
    // outranks a transition on the same property for as long as it applies —
    // so until this class lands, `.vd-hero__text--fade` cannot dim anything
    // and slide copy hard-swaps. Keyed off the LAST of the three to finish
    // (the sub: 240ms delay + 700ms), with a timeout backstop in case the
    // element is absent or its animationend never fires.
    var entranceDone = false;
    function markEntered() {
      if (entranceDone) return;
      entranceDone = true;
      section.classList.add('vd-hero--entered');
    }
    if (subEl) {
      subEl.addEventListener('animationend', markEntered);
    }
    window.setTimeout(markEntered, 1200);

    var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var currentIndex = slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    });
    if (currentIndex < 0) currentIndex = 0;

    var autoplayTimer = null;

    function reducedMotion() {
      return reducedMotionQuery.matches;
    }

    function slideVideo(slide) {
      return slide.querySelector('.vd-hero__video');
    }

    function updateTextField(el, newText) {
      if (!el) return;
      var currentText = el.textContent.trim();
      var nextText = newText || '';

      if (currentText === nextText) return;

      if (reducedMotion()) {
        el.textContent = nextText;
        return;
      }

      el.classList.add('vd-hero__text--fade');
      window.setTimeout(function () {
        el.textContent = nextText;
        el.classList.remove('vd-hero__text--fade');
      }, TEXT_FADE_MS);
    }

    function activate(index) {
      if (index === currentIndex || !slides[index]) return;

      var previousSlide = slides[currentIndex];
      var nextSlide = slides[index];

      // The outgoing slide fades out ON TOP of the incoming one, which is
      // made opaque instantly beneath it (see the .is-leaving rule in
      // velodream.css). Keeps the composite fully opaque for the whole
      // transition instead of dipping in brightness at the midpoint.
      previousSlide.classList.remove('is-active');
      if (!reducedMotion()) {
        previousSlide.classList.add('is-leaving');
        // Clear any pending cleanup from a transition the visitor
        // interrupted by clicking through slides faster than the fade.
        if (previousSlide.vdLeaveTimer) window.clearTimeout(previousSlide.vdLeaveTimer);
        previousSlide.vdLeaveTimer = window.setTimeout(function () {
          previousSlide.classList.remove('is-leaving');
          previousSlide.vdLeaveTimer = null;
        }, CROSSFADE_MS);
      }

      // A slide re-entering while still fading out would otherwise keep
      // `is-leaving` and sit above the stack at opacity 0 — invisible.
      if (nextSlide.vdLeaveTimer) {
        window.clearTimeout(nextSlide.vdLeaveTimer);
        nextSlide.vdLeaveTimer = null;
      }
      nextSlide.classList.remove('is-leaving');
      nextSlide.classList.add('is-active');

      var previousVideo = slideVideo(previousSlide);
      if (previousVideo) {
        previousVideo.pause();
      }

      var nextVideo = slideVideo(nextSlide);
      if (nextVideo && !reducedMotion()) {
        nextVideo.currentTime = 0;
        // play() returns a promise that rejects if the browser blocks
        // autoplay for this element; swallow that rather than throwing an
        // unhandled rejection into the console.
        var playResult = nextVideo.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(function () {});
        }
      }

      dots.forEach(function (dot, dotIndex) {
        var isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      updateTextField(eyebrowEl, nextSlide.dataset.eyebrow);
      updateTextField(headingEl, nextSlide.dataset.heading);
      updateTextField(subEl, nextSlide.dataset.sub);

      currentIndex = index;
    }

    function goToNext() {
      activate((currentIndex + 1) % slides.length);
    }

    function goToPrevious() {
      activate((currentIndex - 1 + slides.length) % slides.length);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      // Freeze the dot progress bar with the rotation it is reporting on,
      // otherwise it keeps filling toward a slide change that isn't coming.
      section.classList.add('vd-hero--paused');
    }

    function startAutoplay() {
      stopAutoplay();
      if (slides.length < 2 || reducedMotion() || document.visibilityState !== 'visible') {
        return;
      }
      section.classList.remove('vd-hero--paused');
      autoplayTimer = window.setInterval(goToNext, AUTOPLAY_INTERVAL_MS);
    }

    function restartAutoplay() {
      // Manual navigation (dot click, swipe, keyboard) resets the
      // countdown so the slide the visitor just chose gets its full
      // dwell time before auto-advancing.
      startAutoplay();
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        activate(dotIndex);
        restartAutoplay();
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    reducedMotionQuery.addEventListener('change', function () {
      startAutoplay();
    });

    // Touch swipe on the media layer.
    if (media) {
      var touchStartX = 0;
      var touchStartY = 0;
      var touchActive = false;

      media.addEventListener(
        'touchstart',
        function (event) {
          if (event.touches.length !== 1) return;
          touchActive = true;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
        },
        { passive: true }
      );

      media.addEventListener(
        'touchend',
        function (event) {
          if (!touchActive) return;
          touchActive = false;

          var touch = event.changedTouches[0];
          var deltaX = touch.clientX - touchStartX;
          var deltaY = touch.clientY - touchStartY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) {
            return;
          }

          if (deltaX < 0) {
            goToNext();
          } else {
            goToPrevious();
          }
          restartAutoplay();
        },
        { passive: true }
      );
    }

    startAutoplay();
  }

  function init() {
    var sections = document.querySelectorAll('[data-vd-hero-carousel]');
    sections.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
